<script>
import axios from 'axios'
import { backUrl } from '@src/config.js'
import { useIndexStore } from '@src/store'
import DateSelector from '@comp/DateSelector.vue'
import moment from 'moment-timezone'

export default {
  name: 'PlanningPopup',
  components: {
    DateSelector,
  },
  props: {
    currentProject: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
    recordingData: {
      type: Object,
      required: true,
    },
  },
  emits: [
    'closePopup',
  ],
  data () {
    return {
      showDialog: this.show,
      startDate: null,
      endDate: null,
      timeSlot: [0, 24],
      daysOfWeek: {
        monday: { title: this.$gettext('Mon'), value: false },
        tuesday: { title: this.$gettext('Tue'), value: false },
        wednesday: { title: this.$gettext('Wed'), value: false },
        thursday: { title: this.$gettext('Thu'), value: false },
        friday: { title: this.$gettext('Fri'), value: false },
        saturday: { title: this.$gettext('Sat'), value: false },
        sunday: { title: this.$gettext('Sun'), value: false },
      },
      frequency: 30,
      timezone: 'Europe/Paris',
      allTimezones: [],
    }
  },
  computed: {
    store () {
      return useIndexStore()
    },
  },
  watch: {
    recordingData: {
      handler() {
        this.populateProjectRecording()
      },
      deep: true,
    },
    show () {
      this.showDialog = this.show
    },
  },
  async mounted () {
    try {
      const response = await axios.get(`${backUrl}all_timezones`)
      this.allTimezones = response.data
    } catch (error) {
      console.error('Error when retrieving timezones: ', error)
    }
  },
  methods: {
    populateProjectRecording () {
      const recordingData = this.recordingData
      this.timeSlot = [
        parseInt(recordingData.day_start_time.slice(0, 2)),
        parseInt(recordingData.day_end_time.slice(0, 2)),
      ]
      this.timezone = recordingData.timezone
      this.startDate = moment.tz(recordingData.recording_start_date, this.timezone)
      this.endDate = moment.tz(recordingData.recording_end_date, this.timezone)
      this.frequency = recordingData.recording_frequency_secs / 60
      this.daysOfWeek.monday.value = recordingData.monday
      this.daysOfWeek.tuesday.value = recordingData.tuesday
      this.daysOfWeek.wednesday.value = recordingData.wednesday
      this.daysOfWeek.thursday.value = recordingData.thursday
      this.daysOfWeek.friday.value = recordingData.friday
      this.daysOfWeek.saturday.value = recordingData.saturday
      this.daysOfWeek.sunday.value = recordingData.sunday
    },
    convertToISOString(dateFromDatePicker) {
      return dateFromDatePicker.tz(this.timezone, true).format()
    },
    async saveRecordingParameters () {
      if (this.endDate < this.startDate) {
        this.store.changeNotification({
          text: this.$gettext('End date must be greater than start date'),
          autoClose: true,
          type: 'error',
        })
        return
      }
      const data = {
        traffic_model: 'best_guess',
        recording_start_date: this.convertToISOString(this.startDate),
        recording_end_date: this.convertToISOString(this.endDate),
        monday: this.daysOfWeek.monday.value,
        tuesday: this.daysOfWeek.tuesday.value,
        wednesday: this.daysOfWeek.wednesday.value,
        thursday: this.daysOfWeek.thursday.value,
        friday: this.daysOfWeek.friday.value,
        saturday: this.daysOfWeek.saturday.value,
        sunday: this.daysOfWeek.sunday.value,
        day_start_time: `${this.timeSlot[0].toString().padStart(2, '0')}:00`,
        day_end_time: `${this.timeSlot[1].toString().padStart(2, '0')}:00`,
        recording_frequency_secs: this.frequency * 60,
        timezone: this.timezone,
      }
      try {
        await axios.post(`${backUrl}projects/${this.currentProject.id}/recording_date_range`, data)
        this.store.changeNotification({
          text: this.$gettext('Recording successfully scheduled'),
          autoClose: true,
          type: 'positive',
        })
      } catch (error) {
        console.error(error)
        this.store.changeNotification({
          text: this.$gettext('An error occurred while scheduling the recording. Please check your data.'),
          autoClose: true,
          type: 'error',
        })
      } finally {
        this.closePopup()
      }
    },
    closePopup () {
      this.$emit('closePopup')
    },
    changeValueOfDay (day) {
      day.value = !day.value
    },
  },
}
</script>
<template>
  <v-dialog
    v-model="showDialog"
    width="33%"
    persistent
  >
    <v-card>
      <v-card-title>
        {{ $gettext('Schedule recording') }}
      </v-card-title>
      <v-card-text>
        <v-autocomplete
          v-model="timezone"
          :label="$gettext('Timezone')"
          :items="allTimezones"
          hide-details
        />
        <div class="dates">
          <DateSelector
            :model-value="startDate"
            :label="$gettext('Start date')"
            @update:model-value="startDate = $event"
          />
          <DateSelector
            :model-value="endDate"
            :label="$gettext('End date')"
            @update:model-value="endDate = $event"
          />
        </div>
        <div>
          <div>{{ $gettext('Time slot') }}</div>
          <v-range-slider
            v-model="timeSlot"
            class="time-range-slider"
            :max="24"
            :min="0"
            :step="1"
            :thumb-size="20"
            thumb-label="always"
            color="primary"
            thumb-color="primary"
            track-color="darkgrey"
            show-ticks="always"
            persistent-hint
            hide-details
          />
        </div>
        <div>
          <div>{{ $gettext('Days of the week') }}</div>
          <div class="days-of-week-selector-container">
            <v-spacer />
            <v-checkbox
              v-for="(day, index) of daysOfWeek"
              :key="index"
              v-model="day.value"
              :label="day.title"
              color="primary"
              hide-details
              @click="changeValueOfDay(day)"
            />
            <v-spacer />
          </div>
        </div>
        <v-select
          v-model="frequency"
          :items="[15, 30, 45, 60]"
          hide-details
          :label="$gettext('Frequency (min)')"
        />
      </v-card-text>
      <v-card-actions>
        <v-btn
          variant="elevated"
          color="secondary"
          :text="$gettext('Cancel')"
          @click="closePopup"
        />
        <v-btn
          variant="elevated"
          color="primary"
          :text="$gettext('Save')"
          @click="saveRecordingParameters"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<style lang="scss" scoped>
.days-of-week-selector-container {
  display: flex;
  flex-direction: row;
  gap: 10px;
}
.v-card-text {
  display: flex;
  flex-direction: column;
  gap: 1ch;
}
.time-range-slider {
  margin-top: 35px;
}
.dates {
  display: flex;
  flex-direction: row;
  gap: 1ch;
}
</style>
